import { useEffect, useState } from "react";
import { API, authFetch } from "@/App";
import { ImageOff, Loader2 } from "lucide-react";

/**
 * <AuthImage> — renders an image stored under /api/files/<path> that requires
 * the session token Authorization header. Falls back to a placeholder while
 * loading and to a broken-image badge on failure.
 *
 * Props:
 *   path       — storage_path (e.g. "pravaah/uploads/collections/<id>/<uuid>.jpg")
 *   className  — passed through to <img> for sizing/borders
 *   alt        — alt text
 *   onClick    — click handler (e.g. open lightbox)
 */
export function AuthImage({ path, alt = "", className = "", onClick }) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    if (!path) {
      setError(true);
      return;
    }
    setError(false);
    setSrc(null);
    (async () => {
      try {
        const res = await authFetch(`${API}/files/${path}`);
        if (!res.ok) throw new Error("fetch failed");
        const blob = await res.blob();
        blobUrl = URL.createObjectURL(blob);
        if (!cancelled) setSrc(blobUrl);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [path]);

  if (error) {
    return (
      <div
        className={`flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-400 ${className}`}
        data-testid="auth-image-error"
      >
        <ImageOff className="w-4 h-4" />
      </div>
    );
  }
  if (!src) {
    return (
      <div
        className={`flex items-center justify-center bg-slate-50 dark:bg-slate-800/50 ${className}`}
        data-testid="auth-image-loading"
      >
        <Loader2 className="w-4 h-4 text-slate-300 animate-spin" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onClick={onClick}
      data-testid="auth-image"
    />
  );
}

export default AuthImage;
